import { Box, Button, Text, TextButton } from '@wix/design-system'
import { useContent, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import { ArrowRight } from '@wix/wix-ui-icons-common'
import React, { FC, useCallback } from 'react'

import { usePageLogger } from '../../../bi/mfi'
import { Button as ReusableButton } from '../../reusable/Button'
import { LogoWithBack } from '../../reusable/LogoWithBack'
import { WixStudioTemplate } from '../../reusable/WixStudioTemplate/WixStudioTemplate'
import { WixStudioTemplateSidePanel } from '../../reusable/WixStudioTemplate/WixStudioTemplateSidePanel'
import type { Content } from './Content'
import { wixStudioSimpleSplitDataHooks as dataHooks } from './WixStudioSimpleSplit.dataHooks'
import s from './WixStudioSimpleSplit.scss'

export const WixStudioSimpleSplit: FC = () => {
  const [isPageLoading] = usePageLoading()
  const next = useNext()
  const { content } = useContent<Content>()
  const { question } = content
  const [primaryOption, secondaryOption, learnMoreOption] = question.options
  const pageLogger = usePageLogger()
  const learnMoreOptionKey = learnMoreOption?.optionKey

  const handlePrimaryClick = async (key: string, value: string) => {
    void pageLogger.answer({ answer: value || '' })
    next({ [key]: { values: value ? [value] : [] } })
  }

  const handleSecondaryClick = async (key: string, value: string) => {
    void pageLogger.answer({ answer: value || '' })
    next({ [key]: { values: value ? [value] : [] } })
  }

  const handleBack = useCallback(async () => {
    await pageLogger.back()
    window.history.back()
  }, [pageLogger])

  const handleLearMoreButtonClick = useCallback(() => {
    if (isPageLoading) {
      return
    }
    void pageLogger.generalAction({ action: learnMoreOptionKey })
    next({ [question.questionKey]: { values: [learnMoreOptionKey] } })
  }, [isPageLoading, learnMoreOptionKey, next, pageLogger, question.questionKey])

  return (
    <>
      <WixStudioTemplate
        logo={
          content.logo === 'WIX' ? <LogoWithBack onBackClick={handleBack} backButton={content.backButton} /> : undefined
        }
        backButton={content.backButton}
        isPageLoading={isPageLoading}
        pageLogger={pageLogger}
        sidePanel={<WixStudioTemplateSidePanel content={[content.sidePanel]} />}
        title={
          <Box maxWidth="630px" marginBottom="SP3" className={s.WixStudioSimpleSplit__title}>
            {content.title}
          </Box>
        }
        bottomNote={
          <TextButton
            dataHook={dataHooks.option({ id: secondaryOption.optionKey })}
            onClick={() => handleSecondaryClick(question.questionKey, secondaryOption.optionKey)}
            underline="always"
          >
            {secondaryOption.label}
          </TextButton>
        }
      >
        {learnMoreOption && (
          <Box position="fixed" top="36px" right="36px" zIndex="1">
            <ReusableButton
              as="button"
              size="tiny"
              priority="secondary"
              onClick={handleLearMoreButtonClick}
              label={learnMoreOption.label}
            />
          </Box>
        )}
        <Box maxWidth="630px" direction="vertical" marginBottom="SP6" align="left">
          {content.text && <Text className={s.WixStudioSimpleSplit__text}>{content.text}</Text>}
          {(content.bullets?.items.length > 0 && (
            <Text listStyle="checkmark">
              <ul>
                {content.bullets?.items.map(bullet => (
                  <li key={bullet.text}>{bullet.text}</li>
                ))}
              </ul>
            </Text>
          )) ||
            null}
        </Box>
        <Box>
          <Button
            suffixIcon={<ArrowRight />}
            onClick={() => handlePrimaryClick(question.questionKey, primaryOption.optionKey)}
            size="medium"
            dataHook={dataHooks.option({ id: primaryOption.optionKey })}
          >
            {primaryOption.label}
          </Button>
        </Box>
      </WixStudioTemplate>
    </>
  )
}
